import React, { useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AuxButton, AuxRadioGroup, AuxTooltip } from "@blk/aladdin-react-components-es";
import { AuxButtonSizeEnum, AuxButtonTypeEnum } from "@blk/aladdin-web-components";
import { InsFromPeriod } from "components/time-period/insFromPeriod";
import { InsDateRange } from "components/time-period/insDateRange";
import { InsLastPeriod } from "components/time-period/insLastPeriod";
import { DataGranularityTooltipWidget } from "components/time-period/insTooltipComponents";
import { InsDashboardButtons } from "./insDashboardButtons";
import { InsTimePeriodHeader } from "./insTimePeriodHeader";
import { language } from "language/default";
import { DataGranularity } from "store/current-dashboard/types";
import { updateDraftWorkspaceSettings } from "store/current-dashboard";
const { quarterly, yearly, yearOverYear } = language.en.skeleton.settingsMenu.granularity;
const TimePeriodTooltipWidget = () => React.createElement(AuxTooltip, null,
    React.createElement(AuxButton, { className: "insSettingsTooltipIcon", icon: "info-bold", size: AuxButtonSizeEnum.XSMALL, slot: "target", type: AuxButtonTypeEnum.TERTIARY_SUBTLE }),
    React.createElement("div", { slot: "content" },
        React.createElement("div", { className: "insTooltipContentWrapper" },
            React.createElement("div", { className: "insTooltipTitle" }, language.en.dashboard.timePeriod.timePeriod),
            React.createElement("div", null, language.en.dashboard.timePeriod.timePeriodTooltip))));
const getGranularityRadioData = (activeGranularity) => [
    {
        checked: activeGranularity === DataGranularity.quarterly,
        disabled: false,
        eventData: DataGranularity.quarterly,
        label: quarterly,
        uid: `${DataGranularity.quarterly}`
    },
    {
        checked: activeGranularity === DataGranularity.yearly,
        disabled: false,
        eventData: DataGranularity.yearly,
        label: yearly,
        uid: `${DataGranularity.yearly}`
    },
    {
        checked: activeGranularity === DataGranularity.yearOverYear,
        disabled: false,
        eventData: DataGranularity.yearOverYear,
        label: yearOverYear,
        uid: `${DataGranularity.yearOverYear}`
    }
];
const checkTimePeriodChanges = (draftSettings, workspaceSettings) => {
    const draftRadio = draftSettings?.dateSettings?.selectedRadio;
    const draftFrom = draftSettings?.dateSettings?.fromPeriod;
    const draftLast = draftSettings?.dateSettings?.lastPeriod;
    const draftLastStep = draftSettings?.dateSettings?.lastPeriodStep;
    const draftStart = draftSettings?.dateSettings?.datePickerStartDate?.toString();
    const draftEnd = draftSettings?.dateSettings?.datePickerEndDate?.toString();
    const draftGranularity = draftSettings?.dataGranularity;
    const wsRadio = workspaceSettings?.dateSettings?.selectedRadio;
    const wsFrom = workspaceSettings?.dateSettings?.fromPeriod;
    const wsLast = workspaceSettings?.dateSettings?.lastPeriod;
    const wsLastStep = workspaceSettings?.dateSettings?.lastPeriodStep;
    const wsStart = workspaceSettings?.dateSettings?.datePickerStartDate?.toString();
    const wsEnd = workspaceSettings?.dateSettings?.datePickerEndDate?.toString();
    const wsGranularity = workspaceSettings?.dataGranularity;
    return draftRadio !== wsRadio
        || draftFrom !== wsFrom
        || draftLast !== wsLast
        || draftLastStep !== wsLastStep
        || draftStart !== wsStart
        || draftEnd !== wsEnd
        || draftGranularity !== wsGranularity;
};
export const InsSettingsTimePeriod = (props) => {
    const { title, subtitle, onApply, onCancel, onReset, isWidgetSettings } = props;
    const dispatch = useDispatch();
    const draftSettings = useSelector((state) => state.currentDashboard?.draftWorkspaceSettings);
    const workspaceSettings = useSelector((state) => state.currentDashboard?.workspaceSettings);
    const activeGranularity = draftSettings?.dataGranularity ?? workspaceSettings?.dataGranularity;
    const handleGranularityChange = useCallback((event) => {
        dispatch(updateDraftWorkspaceSettings({ dataGranularity: event.detail.value.eventData }));
    }, [dispatch]);
    const hasChanges = useMemo(() => checkTimePeriodChanges(draftSettings, workspaceSettings), [draftSettings, workspaceSettings]);
    const isDashboard = onApply && onCancel;
    return (React.createElement("div", null,
        isWidgetSettings
            ? React.createElement("div", { className: "insRadioGroupDiv" },
                React.createElement("span", { className: "insSettingsLabel" }, language.en.skeleton.settingsMenu.timeperiod),
                React.createElement(TimePeriodTooltipWidget, null))
            : React.createElement(InsTimePeriodHeader, { title: title, subtitle: subtitle }),
        React.createElement("div", { className: "content" },
            React.createElement(InsFromPeriod, { ...props }),
            React.createElement(InsLastPeriod, { ...props }),
            React.createElement(InsDateRange, { ...props })),
        !isWidgetSettings && React.createElement(React.Fragment, null,
            React.createElement("div", { className: "insRadioGroupDiv bottom-margin" },
                React.createElement("span", { className: "insSettingsLabel" }, language.en.dashboard.timePeriod.dataGranularity),
                React.createElement(DataGranularityTooltipWidget, null)),
            React.createElement(AuxRadioGroup, { key: "data-granularity", data: getGranularityRadioData(activeGranularity), isStacked: true, onRadioGroupChanged: handleGranularityChange })),
        isDashboard && React.createElement(InsDashboardButtons, { isApplyDisabled: !hasChanges, onApply: onApply, onCancel: onCancel, onReset: onReset })));
};
